import { ArrowRight } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import HourglassImg from '../../../assets/welcoming-slide-7-a.png';
import RocketImg from '../../../assets/welcoming-slide-7-b.png';

interface Slide7Props {
  onComplete?: () => void; // kept for parity with other slides if needed later
}

export default function Slide7({ onComplete }: Slide7Props) {
  const navigate = useNavigate();

  return (
    <div className="animate-in slide-in-from-right duration-300">
      {/* Heading Section */}
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
          A Different Kind of Online Discourse
        </h1>
        <p className="text-sm md:text-base text-gray-700 max-w-3xl mx-0 md:mx-10 leading-relaxed">
          The internet doesn’t have to drain you to keep you connected. The problem isn’t just the
          noise; the systems we use often reward reaction instead of reflection.
        </p>
      </div>

      {/* Top Gradient Card with Hourglass Illustration */}
      <div className="relative mb-5">
        <div className="relative bg-gradient-to-r from-[#F9F6FE] to-[#F4FDF6] border border-[#F3E8FF] rounded-xl px-4 md:px-5 py-3 flex flex-col md:flex-row items-start md:items-center gap-6 shadow-sm w-full md:w-[calc(100%-170px)]">
          <div className="flex-1 md:mr-16">
            {/* Mobile inline image (floated) */}
            <img
              src={HourglassImg}
              alt="Hourglass illustration representing reflective time online"
              className="w-24 sm:w-28 float-right md:mb-2 -mt-5 -mr-7 md:mr-0 rounded-md pointer-events-none select-none md:hidden"
              aria-hidden="true"
            />
            <p className="text-gray-800 text-sm leading-relaxed">
              Twygs was built to shift that balance from doomscrolling to learnscrolling. Each
              scroll becomes a moment to discover something new. Quick, engaging insights make you
              think, start better conversations, and connect both online and in real life.
            </p>
            <p className="text-gray-800 text-sm leading-relaxed mt-3">
              From how ideas are ranked to how the community engages and earns, every feature is
              designed to reward civility, originality, and contribution.
            </p>
          </div>
        </div>
        {/* Desktop absolute image */}
        <img
          src={HourglassImg}
          alt="Hourglass illustration representing reflective time online"
          className="hidden md:block absolute -top-3 right-0 w-40 md:w-64 flex-shrink-0 mx-auto md:mx-0 pointer-events-none select-none"
          aria-hidden="true"
        />
      </div>

      {/* Bottom Section with Rocket Illustration and Goal Statement */}
      <div className="flex flex-col md:flex-row items-start md:items-center">
        {/* Mobile: image and goal statement side-by-side */}
        <div className="flex items-center md:hidden w-full -mx-2">
          <img
            src={RocketImg}
            alt="Rocket and lightbulb illustration signifying ideas taking off"
            className="w-28 flex-shrink-0 pointer-events-none select-none"
          />
          <p className="text-gray-900 font-semibold text-sm leading-relaxed -mr-6">
            Our goal is simple:{' '}
            <span className="font-extrabold">
              make thoughtful discourse not just possible, but rewarding.
            </span>
          </p>
        </div>
        {/* Desktop image */}
        <img
          src={RocketImg}
          alt="Rocket and lightbulb illustration signifying ideas taking off"
          className="hidden md:block w-40 md:w-72 order-1 md:order-none mx-auto md:mx-0 pointer-events-none select-none"
        />
        <div className="flex-1 space-y-3 md:space-y-5">
          {/* Desktop goal statement (hidden on mobile to avoid duplication) */}
          <p className="hidden md:block text-gray-900 font-semibold text-sm leading-relaxed">
            Our goal is simple:{' '}
            <span className="font-extrabold">
              make thoughtful discourse not just possible, but rewarding.
            </span>
          </p>
          <p className="text-gray-700 text-sm leading-relaxed">
            When curiosity and integrity shape the environment, learning follows, communities grow
            stronger, and understanding deepens.
          </p>
          <div className="flex justify-center md:justify-start">
            <button
              onClick={() => {
                navigate('/roadmap');
                onComplete?.();
              }}
              className="inline-flex items-center gap-2 bg-white border border-growth-green hover:border-green-300 text-growth-green hover:bg-green-50 font-semibold px-3 rounded-lg text-sm transition-colors"
            >
              See Our Roadmap to Learn More
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
